import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, Briefcase, KanbanSquare, MessageCircle,
  CalendarClock, User, FileText, Bookmark, LogOut, Bell, Rocket,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import Logo from "@/components/Logo";

const menus = {
  agency: [
    { to: "/dashboard/agency", label: "Overview", icon: LayoutDashboard },
    { to: "/dashboard/agency/candidates", label: "Candidates", icon: Users },
    { to: "/dashboard/agency/employers", label: "Employers", icon: Building2 },
    { to: "/dashboard/agency/jobs", label: "Jobs", icon: Briefcase },
    { to: "/dashboard/agency/pipeline", label: "Pipeline", icon: KanbanSquare },
    { to: "/dashboard/agency/interviews", label: "Interviews", icon: CalendarClock },
    { to: "/dashboard/agency/messages", label: "Messages", icon: MessageCircle },
    { to: "/dashboard/agency/success", label: "Success Center", icon: Rocket },
  ],
  employer: [
    { to: "/dashboard/employer", label: "Overview", icon: LayoutDashboard },
    { to: "/dashboard/employer/jobs", label: "My Jobs", icon: Briefcase },
    { to: "/dashboard/employer/candidates", label: "Candidates", icon: Users },
    { to: "/dashboard/employer/interviews", label: "Interviews", icon: CalendarClock },
    { to: "/dashboard/employer/messages", label: "Messages", icon: MessageCircle },
  ],
  candidate: [
    { to: "/dashboard/candidate", label: "Overview", icon: LayoutDashboard },
    { to: "/dashboard/candidate/profile", label: "My Profile", icon: User },
    { to: "/dashboard/candidate/applications", label: "Applications", icon: FileText },
    { to: "/dashboard/candidate/saved", label: "Saved Jobs", icon: Bookmark },
    { to: "/dashboard/candidate/interviews", label: "Interviews", icon: CalendarClock },
    { to: "/dashboard/candidate/messages", label: "Messages", icon: MessageCircle },
  ],
};

const portalTitle = { agency: "Agency Portal", employer: "Employer Portal", candidate: "Candidate Portal" };

export default function DashboardShell({ portal, children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const menu = menus[portal] || [];
  const [notifs, setNotifs] = useState([]);
  const [openBell, setOpenBell] = useState(false);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { unread_only: false } });
      setNotifs(data.items || []);
    } catch {}
  };
  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 30000); return () => clearInterval(t); }, []);

  const unread = notifs.filter(n => !n.read).length;

  const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-60 bg-navy text-white hidden md:flex md:flex-col z-40">
        <div className="p-5 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo size={34} variant="light" />
            <div>
              <div className="font-display font-semibold">Recruvo</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400">{portalTitle[portal]}</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {menu.map(m => {
            const active = loc.pathname === m.to || (m.to !== "/dashboard/agency" && m.to !== "/dashboard/employer" && m.to !== "/dashboard/candidate" && loc.pathname.startsWith(m.to));
            const Icon = m.icon;
            return (
              <Link key={m.to} to={m.to}
                data-testid={`sidebar-${m.label.toLowerCase().replace(/ /g,'-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors
                  ${active ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
                <Icon className="w-4 h-4" />
                {m.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="text-xs text-slate-400 mb-2 truncate">{user?.email}</div>
          <button onClick={async () => { await logout(); nav("/"); }} data-testid="sidebar-logout"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-gold transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden bg-navy text-white p-4 flex items-center justify-between sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={32} variant="light" />
          <div className="font-display font-semibold">{portalTitle[portal]}</div>
        </Link>
        <button onClick={async () => { await logout(); nav("/"); }} className="text-sm text-slate-300"><LogOut className="w-5 h-5" /></button>
      </div>

      {/* Main */}
      <main className="md:pl-60 min-h-screen">
        {/* Top bar */}
        <div className="hidden md:flex items-center justify-between px-8 py-4 border-b bg-white sticky top-0 z-30">
          <div className="text-sm text-slate-500">
            Signed in as <span className="font-medium text-navy">{user?.name}</span>
          </div>
          <div className="relative">
            <button className="relative p-2 rounded-full hover:bg-slate-100" onClick={() => setOpenBell(!openBell)} data-testid="topbar-notifications">
              <Bell className="w-5 h-5 text-slate-600" />
              {unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 bg-gold text-navy text-[10px] font-bold rounded-full grid place-items-center">{unread}</span>}
            </button>
            {openBell && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg border border-slate-200 shadow-xl overflow-hidden">
                <div className="p-3 flex items-center justify-between border-b">
                  <div className="text-sm font-semibold text-navy">Notifications</div>
                  <button onClick={markAll} className="text-xs text-slate-500 hover:text-navy">Mark all read</button>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifs.length === 0 && <div className="p-4 text-sm text-slate-500">No notifications yet</div>}
                  {notifs.slice(0, 10).map(n => (
                    <Link key={n.id} to={n.link || "#"} className={`block p-3 text-sm border-b last:border-b-0 ${n.read ? "bg-white" : "bg-slate-50"}`}>
                      <div className="font-medium text-navy">{n.title}</div>
                      <div className="text-slate-500 text-xs mt-0.5">{n.body}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 md:p-10">{children}</div>
      </main>
    </div>
  );
}
